import * as _ from 'underscore';
import { IListFetcher } from '../fetchers/IListFetcher';
import { ListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { PermissionsHelper } from '../helpers/PermissionsHelper';
import { PermissionsMerger } from '../helpers/PermissionsMerger';
import { ResultHelper } from '../helpers/ResultHelper';
import { IGranteePermissions } from '../models/IGranteePermissions';
import { Module } from '../Module';
import { IGetEffectivePermissionsResult } from '../ServerApi/requests/GetEffectivePermissionsRequest';
import { GetUsersRequest, IGetUsersResult } from '../ServerApi/requests/GetUsersRequest';
import { IGetItemsParams } from '../ServerApi/types/IGetItemsParams';
import { ITypedIds } from '../ServerApi/types/ITypedIds';
import { IUser, UserKeys } from '../ServerApi/types/IUser';
import { FetcherFactory } from './FetcherFactory';
import { ServerService } from './ServerService';

export class EffectivePermissionsService {

  private $q: ng.IQService;
  private server: ServerService;
  private usersFetcher: IListFetcher<IUser>;

  public static $inject = ['$q', 'ServerService', 'FetcherFactory'];

  constructor($q: ng.IQService, server: ServerService, FetcherFactory: FetcherFactory) {
    this.$q = $q;
    this.server = server;
    this.usersFetcher = FetcherFactory.getServerResourceFetcher(
      GetUsersRequest,
      (result: IGetUsersResult) => result.users);
  }

  public getEffectivePermissions(
    usersParams: IGetItemsParams,
    authorizables: ITypedIds
  ): ng.IPromise<ListResult<IGranteePermissions>> {
    return this.usersFetcher.fetchList(usersParams)
      .then<ListResult<IGranteePermissions>>((users: ListResult<IUser>) => {
        if (users.items.length === 0) {
          // no users; return an empty ListResult
          return this.$q.when(new ListResult<IGranteePermissions>([]));
        } else {
          // we have users; request & return the effective permissions
          const userIds = _.pluck(users.items, UserKeys.id);

          const requests = PermissionsHelper.effectivePermissionsRequestsForAuthorizables(userIds, authorizables);
          const combineResultsFn = (results: IGetEffectivePermissionsResult[]) => {
            return { permissions: ResultHelper.combine(results, (result) => result.permissions) };
          };

          return this.server.sendRequestsParallel(requests, combineResultsFn)
            .then<ListResult<IGranteePermissions>>((result: IGetEffectivePermissionsResult) => {
              const list = PermissionsMerger.makeGranteePermissionsList(
                result.permissions, authorizables.ids, [], users.items);
              return new ListResult<IGranteePermissions>(list, users.index, users.total);
            });
        }
      });
  }

  public getProjectLeaderCapability(userId: string, projectId: string): ng.IPromise<boolean> {
    const contentType = 'project';
    const userParams = { filter: Filter.and([ Filter.fieldEq('id', userId) ]) };
    const authorizables = { type: contentType, ids: [ projectId ] };

    return this.getEffectivePermissions(userParams, authorizables)
      .then((result: ListResult<IGranteePermissions>) => PermissionsHelper.hasProjectLeaderCapability(result.items[0]));
  }

}

Module.instance().service('EffectivePermissionsService', EffectivePermissionsService);
